import useLocalStore from "../store/useLocalStore";
import {useEffect, useRef, useState} from "react";
import matchlog from "../utils/matchlog";
import useWallet from "./useWallet";
import {WalletReceiveMessageType} from "../types";
import {sendMessage} from "../utils/wallet";
import useStore from "../store/useStore";
import {encodeBase64, getAppClientId} from "../utils";
import {WalletConfigType} from "../types/types";
import useUserInfo from "./useUserInfo";
import {getLocale} from "../store";

const getWalletIframe = () => {
    return document.getElementById('match-wallet') as HTMLIFrameElement
}
const AppClientId = getAppClientId()

export default function useWalletInit({
                                          config
                                      }: {
    config: WalletConfigType
}) {
    const {endpoints, token, setWallet, appid,refreshOverview} = useLocalStore();
    const {setWalletReady, walletReady} = useStore()
    const iframeReadyRef = useRef(walletReady)
    useEffect(() => {
        setWallet(config)
    }, [config]);
    useEffect(() => {
        matchlog.log('sdk.mpc.status', walletReady, iframeReadyRef.current)
        iframeReadyRef.current = walletReady
        if (iframeReadyRef.current) {
            const onReady = async () => {
                if (window.matchProvider.walletMessageStack.length > 0) {
                    const stack = window.matchProvider.walletMessageStack
                    window.matchProvider.walletMessageStack = []
                    for (let i = 0; i < stack.length; i++) {
                        const item = stack[i]
                        sendMessage(item)
                    }
                }
            }
            onReady()
        }
    }, [walletReady]);
    //create an iframe in page body
    useEffect(() => {
        if (!endpoints.auth || !appid || !token || !config) {
            const existingIframe = getWalletIframe();
            if (existingIframe) {
                //remove iframe
                existingIframe.remove()
            }
            return
        }
        if (endpoints.auth) {
            const existingIframe = getWalletIframe();
            const query = {
                appid,
                appClientId: AppClientId,
                initParams:encodeBase64(JSON.stringify({
                    access_token: token,
                    type:config.type
                }))
            }
            const url = endpoints.auth + 'wallet/2?' + new URLSearchParams(query).toString()
            if (!existingIframe) {
                const createIframe = () => {
                    const iframe = document.createElement('iframe');
                    iframe.id = 'match-wallet';
                    iframe.src = url
                    iframe.style.display = 'none';
                    iframe.style.width = '0';
                    iframe.style.height = '0';
                    iframe.style.position = 'fixed';
                    iframe.style.zIndex = '200'
                    // iframe.style.background = "rgba(45, 60, 82, 0.50)"
                    // iframe.style.backdropFilter = "blur(4px)"
                    //@ts-ignore
                    //add on error console
                    iframe.onerror = (e) => {
                        console.error('sdk.mpc.error', e)
                    }
                    document.body.insertBefore(iframe, document.body.firstChild);
                }
                createIframe();
                if (!window.matchProvider) {
                    window.matchProvider = {
                        transactionMessageIntervalMap: {},
                        walletMessageMap: {},
                        walletMessageStack: [],
                        sendWalletMessage: ({
                                                method,
                                                data = {},
                                            }) => {

                            const messageId = Date.now().toString() + Math.random().toString().slice(6)
                            const message = {
                                method,
                                data,
                                messageId,
                                source: "matchid",
                                version: 1,
                                locale:getLocale()
                            }

                            try {
                                matchlog.log('sdk.mpc.send', message, endpoints.auth)
                                getWalletIframe()?.contentWindow?.postMessage(message, endpoints.auth);
                            } catch (error) {
                                console.error('sdk.mpc.sendError', error)
                            }
                            return messageId
                        },
                        waitUntilWalletMessage: async ({
                                                           method,
                                                           data = {},
                                                           timeout = 30000
                                                       }) => {
                            return new Promise((resolve, reject) => {
                                try {
                                    if (iframeReadyRef.current && window.matchProvider.walletMessageStack.length == 0) {
                                        sendMessage({
                                            method,
                                            data,
                                            resolve,
                                            reject,
                                            timeout
                                        })
                                        return
                                    }
                                    //if iframe not ready or stack has data need to send, push to stack
                                    window.matchProvider.walletMessageStack.push({
                                        method,
                                        data,
                                        timeout,
                                        resolve,
                                        reject
                                    })
                                } catch (error: any) {
                                    reject(error)
                                }
                            })
                        }
                    }
                }

            } else {
                if (existingIframe.src !== url) {
                    setWalletReady(false);
                    existingIframe.src = url
                }
            }
        }

    }, [endpoints.auth, appid, token, config]);
    useEffect(() => {
        const messageHandle = async (e: MessageEvent) => {
            //origin
            if (e.origin !== endpoints.auth.substring(0, endpoints.auth.length - 1)) {
                return
            }
            const res = e.data as WalletReceiveMessageType
            if (res.source != 'match-wallet') {
                return
            }
            matchlog.log('sdk.mpc.receive', e)

            const messageId = res.messageId
            if (messageId == 'ready') {
                matchlog.log('sdk.mpc.ready')
                setWalletReady(true)
                window.matchProvider.sendWalletMessage({
                    method: 'readied'
                })
                //send readied message
                return
            }
            if (messageId == 'openIframe') {
                const iframe = getWalletIframe()
                if (iframe) {
                    iframe.style.display = 'block'
                    iframe.style.width = '100vw'
                    iframe.style.height = '100vh'
                    iframe.style.right = '0'
                    iframe.style.top = '0'
                }
                return
            }
            if (messageId == 'closeIframe') {
                const iframe = getWalletIframe()
                if (iframe) {
                    iframe.style.display = 'none'
                    iframe.style.width = '0'
                    iframe.style.height = '0'
                    iframe.style.right = '0'
                    iframe.style.top = '0'
                }
                return
            }
            if (messageId == 'refreshOverview') {
                refreshOverview()
                return
            }
            if (messageId == 'updateTransactionData') {
                const {data, rootData, transactionId} = res.data
                if (window.matchProvider.transactionMessageIntervalMap[transactionId] && window.matchProvider.transactionMessageIntervalMap[transactionId].transaction.data == rootData) {
                    window.matchProvider.transactionMessageIntervalMap[transactionId].transaction.data = data
                    const {func} = window.matchProvider.transactionMessageIntervalMap[transactionId]
                    func()
                }
                return
            }
            if (messageId && window.matchProvider.walletMessageMap[messageId]) {
                const {resolve, reject, timeout} = window.matchProvider.walletMessageMap[messageId]
                clearTimeout(timeout!)
                if (res.method == 'ping') {
                    delete window.matchProvider.walletMessageMap[messageId].timeout
                    return
                }
                delete window.matchProvider.walletMessageMap[messageId]
                if (res.status == 'success') {
                    resolve(res.data)
                } else {
                    console.error('sdk.mpc.fail', res)
                    reject(new Error(res.data.message))
                }
            }
        }
        window.addEventListener('message', messageHandle)
        return () => {
            window.removeEventListener('message', messageHandle)
        }

    }, []);

    return {
        walletReady
    }
}